import React from 'react';
import { Checkbox, Input, Radio, Row, Form, Button } from 'antd';
import PropTypes from 'prop-types';
import * as yup from 'yup';

import './ControlWithOptions.scss';
import PlusIcon from 'components/Icons/PlusIcon';
import ClearXIcon from 'components/Icons/ClearXIcon';

// Yup validation schema
const validationSchema = yup.object().shape({
  key: yup
    .string()
    .required('Header is required')
    .max(300, 'Header must be less than 300 characters')
    .trim(),
  options: yup.array().of(
    yup.object().shape({
      label: yup
        .string()
        .required('Option is required')
        .max(150, 'Option must be less than 150 characters')
        .trim(),
      value: yup.string(),
    })
  ),
});

const ControlWithOptions = ({
  item,
  isRadio = true,
  onChange,
  disabled = false,
  configText,
  isRTL = false,
}) => {
  const [form] = Form.useForm();

  const handleFormSubmit = (values) => {
    const changeData = {
      key: values.key,
      options: values.options,
      type: isRadio ? 'radio' : 'checkbox',
    };
    onChange?.(changeData);
  };

  const yupAddSync = {
    async validator({ field }, value) {
      const allValues = form.getFieldsValue();
      const keys = field.split('.');
      const lastKey = keys.pop(); // Get the last key
      const lastObject = keys.reduce((acc, part) => acc[part], allValues);
      if (lastObject && lastKey in lastObject) {
        lastObject[lastKey] = value;
      }
      await validationSchema.validateAt(field, allValues, { context: allValues });
    },
  };

  const renderOptionInput = (name, index, fieldKey, remove) => {
    return (
      <Form.Item
        key={index}
        name={[name, 'label']}
        fieldKey={[fieldKey, 'label']}
        className="option-form-item"
        rules={[yupAddSync]}>
        <OptionInput
          configText={configText}
          isRadio={isRadio}
          option={item.options[index]}
          remove={remove}
          fieldName={name}
        />
      </Form.Item>
    );
  };

  return (
    <Form
      form={form}
      initialValues={item}
      onFinish={handleFormSubmit}
      layout="vertical"
      className="input-option-form">
      <Form.Item name="key" fieldKey={'key'} rules={[yupAddSync]}>
        <QuestionInput configText={configText} item={item} />
      </Form.Item>

      <div className="options-section">
        <Form.List name="options">
          {(fields, { add, remove }) => (
            <>
              <div className="options-list">
                {fields.map(({ key, name, fieldKey, ...restField }) =>
                  renderOptionInput(name, key, fieldKey, fields.length > 1 ? remove : null)
                )}
              </div>
              <div className="add-option-button">
                <Row
                  className={fields.length >= 10 ? 'disabled' : ''}
                  onClick={
                    fields.length >= 10
                      ? null
                      : () =>
                          add({
                            value: '',
                            label: `${configText('option', true)} ${fields.length + 1}`,
                          })
                  }
                  align="middle"
                  style={{ width: 'fit-content' }}>
                  <PlusIcon />
                  {configText ? configText('Add option', true) : 'Add option'}
                </Row>
              </div>
            </>
          )}
        </Form.List>
      </div>

      <Form.Item className="submit-button-container">
        <Row className="footer" justify="end">
          <Button className="medium-btn secondary-btn" onClick={() => form.submit()}>
            Save
          </Button>
        </Row>
      </Form.Item>
    </Form>
  );
};

const QuestionInput = ({ configText, item, ...props }) => {
  return (
    <>
      <div className="ant-form-item-label">
        <span>{item.type === 'radio' ? 'Radio button header' : 'Checkbox header'}</span>
      </div>
      <Input.TextArea
        placeholder={configText ? configText('Enter title', true) : 'Enter title'}
        className="question-input"
        size="large"
        rows={2}
        style={{ resize: 'none' }}
        autoSize={false}
        {...props}
      />
    </>
  );
};

const OptionInput = ({ configText, isRadio, option, remove, fieldName, ...props }) => {
  return (
    <>
      <Row style={{ flexWrap: 'nowrap', alignItems: 'center', gap: '4px' }}>
        {isRadio ? <Radio checked={option?.checked} /> : <Checkbox checked={option?.checked} />}
        <Input
          placeholder={configText ? configText('Enter option', true) : 'Enter option'}
          className="option-value-input"
          size="small"
          style={{ marginLeft: '4px' }}
          {...props}
        />
        {remove && (
          <div className="remove-option-icon" onClick={() => remove(fieldName)}>
            <ClearXIcon />
          </div>
        )}
      </Row>
    </>
  );
};

ControlWithOptions.propTypes = {
  question: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string,
    })
  ),
  isRadio: PropTypes.bool,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  configText: PropTypes.func,
  className: PropTypes.string,
};

ControlWithOptions.defaultProps = {
  question: '',
  options: [],
  isRadio: true,
  disabled: false,
  required: false,
  className: '',
};

export default ControlWithOptions;
